import { FileWarning } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';

import { GatedChrome, RestrictedPanel } from '@/components/auth/GatedChrome';
import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { usePermission, usePermissionState } from '@/hooks/usePermission';

import { DocumentTypeAccess } from './components/DocumentTypeAccess';
import { PermissionMatrix } from './components/PermissionMatrix';
import { ProviderGrants } from './components/ProviderGrants';
import { RolesAccessSkeleton } from './components/RolesAccessSkeleton';
import { useRolesMatrix } from './use-roles-access';
import { StatePanel } from '@/components/ui/StatePanel';
import { errorAdvice } from '@/lib/error-advice';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Configuración' },
  { label: 'Roles y accesos', strong: true },
];

function Frame({ children }: { children: ReactNode }): ReactElement {
  // G4: sin permiso confirmado, las migas y el encabezado no nombran la pantalla.
  const perm = usePermissionState('system:roles:manage');
  return (
    <>
      <GatedChrome state={perm.state} breadcrumb={BREADCRUMB} navContext="Roles y accesos" />
      {children}
    </>
  );
}

export function RolesAccess(): ReactElement {
  const perm = usePermissionState('system:roles:manage');
  const canManage = perm.state === 'granted';
  const query = useRolesMatrix({ enabled: canManage });
  // Reglas por tipo de documento (spec 2026-10-14 §8 D9): solo quien las administra.
  const canManageTypeAccess = usePermission('quality:document-access:manage');

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (perm.state === 'denied') {
    return (
      <Frame>
        <RestrictedPanel />
      </Frame>
    );
  }

  if (perm.state === 'loading' || query.isLoading) {
    return (
      <Frame>
        <RolesAccessSkeleton />
      </Frame>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Frame>
        <StatePanel
          icon={<FileWarning />}
          title="No se pudo cargar la matriz"
          tone="danger"
          role="alert"
          onRetry={() => void query.refetch()}
        >
          {errorAdvice(query.error)}
        </StatePanel>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="card rise">
        <div className="ch">
          <h3>Matriz de permisos · rol × capacidad</h3>
          <span className="count">Referencia · lo que puede hacer cada rol</span>
        </div>
        <p
          className="soft"
          style={{ fontSize: 12.5, padding: '0 16px 12px', margin: 0, lineHeight: 1.5 }}
        >
          Los roles y sus permisos son comunes a todas las empresas del grupo.
        </p>
        <PermissionMatrix matrix={query.data} />
      </div>
      {canManageTypeAccess && <DocumentTypeAccess />}
      <div className="card rise" style={{ marginTop: 16 }}>
        <div
          className="cb"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}
        >
          <p className="soft" style={{ margin: 0, flex: '1 1 260px', fontSize: 13 }}>
            Los roles de cada persona se asignan en Usuarios.
          </p>
          <Link className="btn sm mut" to="/configuracion/usuarios">
            Ir a Usuarios
          </Link>
        </div>
      </div>
      <ProviderGrants />
    </Frame>
  );
}
